<script setup lang="ts">
import {
  Bell03,
  MessageDotsSquare,
  Users03,
  AlertCircle,
  UserCircle,
  Bookmark,
  Clipboard,
  Phone
} from "utruckin-icons";
import {useRoute, useRouter} from "vue-router";
import {ref, Component} from "vue";

interface IMainItem {
  name: string;
  router: string;
  icon: Component;
}

const mainItems: IMainItem[] = [
  {
    name: "Messages",
    router: "/message",
    icon: Bell03,
  },
  {
    name: "Chats",
    router: "/chat",
    icon: MessageDotsSquare,
  },
  {
    name: "Groups",
    router: "/group",
    icon: Users03,
  },
  {
    name: "Errors",
    router: "/errors",
    icon: AlertCircle,
  },
  {
    name: "Accounts",
    router: "/account",
    icon: UserCircle,
  },
  {
    name: "Saved",
    router: "/saved",
    icon: Bookmark,
  },
  {
    name: "Shifts",
    router: "/shifts",
    icon: Clipboard,
  },
  {
    name: "Contacts",
    router: "/contacts",
    icon: Phone,
  },
];

const router = useRouter();
const route = useRoute();

const isWide = ref<boolean>(true);

const goToPage = (item: IMainItem) => {
  router.push(item.router);
};

const goToHome = () => {
  router.push("/");
}
</script>

<template>
  <div class="c-w-sidebar" :class="isWide ? 'wide' : ''">
    <div class="header">
      <img
          v-if="isWide"
          src="@/assets/images/logo.png"
          width="142"
          height="36"
          alt="company_logo"
          @click="goToHome"
      />
      <img
          v-else
          width="38"
          height="32"
          src="@/assets/images/logo_small.png"
          alt="company_logo_small"
          @click="goToHome"
      />

      <div
          class="header__icon"
          @click="
          () => {
            isWide = !isWide;
          }
        "
      >
        <img
            v-if="isWide"
            src="@/assets/icons/arrow-circle-broken-right.svg"
            alt="arrow-circle-broken-right"
        />
        <img
            v-else
            src="@/assets/icons/arrow-circle-broken-left.svg"
            alt="arrow-circle-broken-left"
        />
      </div>
    </div>

    <div class="body">
      <div v-for="(item, index) in mainItems" :key="index" class="body__list">
        <div
            class="body__list--item"
            :class="route.path == item.router ? 'active' : ''"
            @click="goToPage(item)"
        >
          <component class="icon" :is="item.icon" :size="20"/>
          {{ isWide ? item.name : "" }}
        </div>

        <div v-if="route.path == item.router" class="body__list--child"></div>
        <div v-if="!isWide" class="body__list--hover">
          {{ item.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.c-w-sidebar {
  width: 72px;
  border-right: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  height: 100vh;
  transition: 0.2s linear;

  &.wide {
    width: 200px;

    .header {
      height: 72px;
      justify-content: left;
      padding: 18px 16px;
    }
  }

  .header {
    height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 22px 20px;
    border-bottom: 1px solid var(--color-border);
    position: relative;

    img {
      cursor: pointer;
    }

    &__icon {
      position: absolute;
      background: var(--color-background);
      width: 28px;
      height: 28px;
      left: calc(100% - 14px);
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
    }
  }

  .body {
    height: 100%;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    &__list {
      position: relative;

      &--item {
        padding: 10px;
        cursor: pointer;
        border-radius: 14px;
        display: flex;
        gap: 10px;
        align-items: center;
        font-weight: 600;
        color: var(--color-label);

        &.active {
          background: var(--color-primary);
          color: var(--color-white) !important;
        }

        &:hover {
          background-color: var(--color-bg-secondary);
        }
      }

      &--child {
        position: absolute;
        right: -16px;
        top: calc(50% - 13px);
        width: 4px;
        height: 26px;
        background: var(--color-primary);
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }

      &--hover {
        display: none;
        position: absolute;
        left: calc(100% + 8px);
        top: calc(50% - 13px);
        z-index: 2;
        padding: 4px 8px;
        background: var(--color-primary);
        box-shadow: 0 4px 4px 0 #00000040;
        color: var(--color-white);
        border-radius: 8px;
      }

      &:hover .body__list--hover {
        display: flex;
      }
    }
  }

  .footer {
    height: 72px;
    padding: 16px;
    border-top: 1px solid var(--color-border);

    &__item {
      padding: 10px;
      cursor: pointer;
      border-radius: 14px;
      display: flex;
      gap: 10px;
      align-items: center;

      &:hover {
        background-color: var(--color-hover);
      }
    }
  }
}
</style>
